import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import type { TicketTierDto } from '../api/tickets.api';

/** Aceeasi limita ca `@Size(max = 1000)` pe `TicketTierDTO.descriere` (backend). */
export const DESCRIERE_MAX = 1000;

/**
 * `availableFrom`/`availableTo` sunt `LocalDate` pe backend, deci `YYYY-MM-DD` pe sarma - exact ce
 * produce `<input type="date">`. Nu se convertesc in `Date`: tipul generat spune `Date`, dar
 * `transformers.gen.ts` nu ruleaza niciodata.
 */
const schema = z.object({
    // Limita e aceeasi cu `MAX_NIVELURI` din `TicketTiersAdminPage`, dar traieste
    // separat: schema valideaza un camp, pagina numara randuri. Cine urca una
    // fara cealalta obtine un formular care refuza ce pagina permite, sau invers.
    //
    // 🔴 SI MAI E UNA, A TREIA, IN BACKEND — cea care decide de fapt:
    // `dto/TicketTierDTO.java` are `@Min(1) @Max(4)` pe `level`, iar
    // `TicketTierController` primeste `@Valid` si la `create`, si la `update`.
    // Deci pana cand adnotarea aia urca la 6, un nivel 5 sau 6 pleaca de aici si
    // se intoarce cu 400 «Nivelul este intre 1 si 4.» — vizibil, ca toast, nu
    // tacut. Tipurile generate NU spun asta: `TicketTierDto.level` e un `number`
    // fara minim si fara maxim in `types.gen.ts`.
    level: z.coerce.number().int().min(1, 'Între 1 și 6.').max(6, 'Între 1 și 6.'),
    name: z.string().min(1, 'Nu este completat.').max(120),
    pointsCost: z.coerce.number().int().min(1, 'Cel puțin 1 punct.'),
    couponValue: z.coerce.number().min(0),
    availableFrom: z.string().optional().or(z.literal('')),
    availableTo: z.string().optional().or(z.literal('')),
    maxPerSchool: z.union([z.coerce.number().int().min(1), z.literal('')]).optional(),
    descriere: z.string().max(DESCRIERE_MAX, `Cel mult ${DESCRIERE_MAX} de caractere.`).optional().or(z.literal('')),
});
type FormValues = z.infer<typeof schema>;

interface TierFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    editionId: string;
    initial?: TicketTierDto;
    onSubmit: (dto: TicketTierDto) => Promise<unknown>;
}

export function TierFormDialog({ open, onOpenChange, editionId, initial, onSubmit }: TierFormDialogProps) {
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        values: {
            level: initial?.level ?? 1,
            name: initial?.name ?? '',
            pointsCost: initial?.pointsCost ?? 100,
            couponValue: initial?.couponValue ?? 0,
            availableFrom: (initial?.availableFrom as unknown as string) ?? '',
            availableTo: (initial?.availableTo as unknown as string) ?? '',
            maxPerSchool: initial?.maxPerSchool ?? '',
            descriere: initial?.descriere ?? '',
        },
    });
    const lungimeDescriere = form.watch('descriere')?.length ?? 0;

    const submit = form.handleSubmit(async (v) => {
        await onSubmit({
            id: initial?.id,
            editionId,
            level: v.level,
            name: v.name,
            pointsCost: v.pointsCost,
            couponValue: v.couponValue,
            // Sirul gol inseamna „fara capat", nu „capat vid": trimis ca atare, backendul l-ar
            // respinge la parsarea LocalDate.
            availableFrom: (v.availableFrom || undefined) as unknown as TicketTierDto['availableFrom'],
            availableTo: (v.availableTo || undefined) as unknown as TicketTierDto['availableTo'],
            maxPerSchool: v.maxPerSchool === '' || v.maxPerSchool === undefined ? undefined : Number(v.maxPerSchool),
            descriere: (v.descriere || undefined) as unknown as TicketTierDto['descriere'],
        });
        onOpenChange(false);
    });

    const camp = (name: keyof FormValues, label: string, type = 'text') => (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    <FormLabel>{label}</FormLabel>
                    <FormControl>
                        <Input type={type} {...field} value={field.value as string | number} />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            )}
        />
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* dvh, nu vh: pe mobil bara de adresa face vh sa taie ultimul camp si butonul. */}
            <DialogContent className="max-h-[90dvh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{initial ? 'Editează nivelul' : 'Nivel nou'}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={submit} className="space-y-4">
                        {camp('level', 'Nivel (1-6)', 'number')}
                        {camp('name', 'Denumire')}
                        {camp('pointsCost', 'Cost în puncte', 'number')}
                        {camp('couponValue', 'Valoare cupon (lei)', 'number')}
                        {camp('availableFrom', 'Disponibil de la', 'date')}
                        {camp('availableTo', 'Disponibil până la', 'date')}
                        {camp('maxPerSchool', 'Maxim per școală (gol = nelimitat)', 'number')}
                        <FormField
                            control={form.control}
                            name="descriere"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel htmlFor="descriere-nivel">Descriere (opțional)</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            id="descriere-nivel"
                                            {...field}
                                            maxLength={DESCRIERE_MAX}
                                            aria-describedby="descriere-nivel-indiciu descriere-nivel-contor"
                                            className="min-h-24"
                                        />
                                    </FormControl>
                                    {/* Item 15: o linie = o optiune de premiu. Cu mai multe linii,
                                        profesorul alege una (radio obligatoriu) la revendicare; cu una,
                                        e aleasa automat. Regula sta in `utils/optiuni-premiu.ts`. */}
                                    <p id="descriere-nivel-indiciu" className="m-0 text-smaller text-gray-600">
                                        O linie = o opțiune de premiu. Cu mai multe linii, profesorul alege una la revendicare.
                                    </p>
                                    {/* Contor pasiv (`aria-live="off"`): un cititor de ecran nu are
                                        de ce sa anunte fiecare caracter tastat -- doar eroarea de
                                        validare (mai jos, prin FormMessage) merita anuntata. */}
                                    <p
                                        id="descriere-nivel-contor"
                                        aria-live="off"
                                        className="text-right text-smaller text-gray-600 m-0"
                                    >
                                        {lungimeDescriere}/{DESCRIERE_MAX}
                                    </p>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {/*
                          * „Anulez" NU e decorativ: singura alta iesire din dialog e X-ul de 16px
                          * din `ui/dialog.tsx` (default shadcn), adica sub 40% din tinta de 44.
                          * Pe telefon nu exista Escape, iar clicul in afara nu e o afordanta
                          * vizibila - nimeni nu o ghiceste. Fara butonul asta, un admin care
                          * deschide din greseala nivelul gresit ramane blocat intr-un formular
                          * cu 7 campuri.
                          */}
                        <DialogFooter className="gap-2 sm:gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                className="touch-target min-h-12 w-full sm:w-auto"
                                disabled={form.formState.isSubmitting}
                                onClick={() => onOpenChange(false)}
                            >
                                Anulez
                            </Button>
                            <Button
                                type="submit"
                                className="touch-target min-h-12 w-full bg-magenta hover:bg-magenta-hover sm:w-auto"
                                disabled={form.formState.isSubmitting}
                            >
                                Salvează
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
